import { captureRemixErrorBoundaryError } from "@sentry/remix";
import {
  QueryClient,
  QueryClientProvider as QueryClientProviderOdTana,
} from "@tanstack/react-query";
import { TRPCClientError } from "@trpc/client";
import { Toaster, TooltipProvider } from "@ui/components";
import { observer } from "mobx-react-lite";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useRouteError,
  data,
} from "react-router";
import { type LoaderFunctionArgs } from "react-router";
import { useChangeLanguage } from "remix-i18next/react";

import { AuthContextProvider, useAuthStore } from "./auth/AuthContext";
import { GlobalNavigationProgress } from "./components/GlobalNavigationProgress";
import { HeaderLogo } from "./components/HeaderLogo";
import { getSession } from "./getSession.server";
import { getLocale, i18nCookie } from "./I18N/i18n.server";
import { LanguageSwitchProvider } from "./I18N/LanguageSwitchContext";
import { RootLayout } from "./layout/RootLayout";

import "./globals.css";

export function meta() {
  return [
    {
      title: "sztama",
    },
  ];
}

export async function loader({ request, context }: LoaderFunctionArgs) {
  let headers = new Headers();
  const [authData, locale] = await Promise.all([
    getSession(request, context, (error) => {
      if (error instanceof TRPCClientError) {
        const response = error.meta?.response;
        if (
          response !== null &&
          response !== undefined &&
          typeof response === "object" &&
          "headers" in response &&
          response.headers !== null &&
          response.headers !== undefined &&
          typeof response.headers === "object"
        ) {
          headers = new Headers(response.headers as Record<string, string>);
        }
      }
    }),
    getLocale(request),
  ]);

  const cookie = await i18nCookie.serialize(locale);
  headers.append("Set-Cookie", cookie);

  return data({ authData, locale }, { headers: headers });
}

interface DocumentProps {
  lang?: string;
}

function Document(props: React.PropsWithChildren<DocumentProps>) {
  return (
    <html lang={props.lang}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {props.children}
        <Toaster />
        <GlobalNavigationProgress />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

const PosthogInit = observer(function PosthogInit() {
  const authStore = useAuthStore();

  useEffect(() => {
    const token =
      process.env.NODE_ENV === "development"
        ? "fake_token"
        : "phc_4rh7rRreHytvZIzYtXhCEFr0xpzQuaL2Bimnu8kf1T2";

    posthog.init(token, {
      api_host: "https://us.i.posthog.com",
      person_profiles: "always",
    });
  }, []);

  useEffect(() => {
    if (!authStore.isAuthenticated) {
      return;
    }

    console.info("[PostHog] Identify");
    posthog.identify(
      authStore.user.email ?? authStore.user.id,
      {
        email: authStore.user.email,
        name: authStore.user.name,
        isAthlete: authStore.isAthlete,
        isCoach: authStore.isCoach,
      },
      {
        invitationCode: authStore.authenticatedData.invitationCode ?? "none",
      },
    );
    posthog.capture("Authenticated", {
      distinctId: authStore.user.email ?? authStore.user.id,
    });
  }, [authStore.isAuthenticated]);

  return null;
});

const queryClient = new QueryClient();

export default function App() {
  const loaderData = useLoaderData<typeof loader>();
  useChangeLanguage(loaderData.locale);

  return (
    <Document lang={loaderData.locale}>
      <QueryClientProviderOdTana client={queryClient}>
        <LanguageSwitchProvider>
          <AuthContextProvider
            authenticatedData={loaderData?.authData}
            key={
              loaderData?.authData?.user.id +
              "-" +
              loaderData?.authData?.user.tier
            }
          >
            <TooltipProvider>
              <RootLayout>
                <Outlet />
              </RootLayout>
            </TooltipProvider>
            <PosthogInit />
          </AuthContextProvider>
        </LanguageSwitchProvider>
      </QueryClientProviderOdTana>
    </Document>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  captureRemixErrorBoundaryError(error);

  console.error(error);

  return (
    <Document lang="en">
      <div className="flex flex-col h-full">
        <HeaderLogo />
        <div className="grow text-xl h-full flex items-center justify-center">
          {isRouteErrorResponse(error)
            ? `${error.status} ${error.statusText}`
            : error instanceof Error
              ? error.message
              : "Unknown Error"}
        </div>
      </div>
    </Document>
  );
}
